// =============================================================================
// SimSense Platform — Desktop Agent Store (Zustand)
// =============================================================================

import { create } from "zustand";
import { isTauri as isTauriRuntime } from "@tauri-apps/api/core";
import { remoteLog } from "../utils/logger";

interface FileCreatedPayload {
  path: string;
  name: string;
}

interface IbtFileInfo {
  path: string;
  name: string;
  size: number;
  modified_epoch: number;
}

interface RetryFile {
  path: string;
  name: string;
  retries: number;
  nextAttempt: number;
}

let isInitialized = false;
let removeFileListener: null | (() => void) = null;

// ---------------------------------------------------------------------------
// Persistent "already uploaded" tracking via localStorage
// ---------------------------------------------------------------------------
const UPLOADED_KEY = "osc_uploaded_files";

function getUploadedFiles(): Set<string> {
  try {
    const raw = localStorage.getItem(UPLOADED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markFileUploaded(name: string) {
  const set = getUploadedFiles();
  set.add(name);
  // Cap at 500 entries to avoid unbounded growth
  const arr = [...set];
  if (arr.length > 500) arr.splice(0, arr.length - 500);
  localStorage.setItem(UPLOADED_KEY, JSON.stringify(arr));
}

// ---------------------------------------------------------------------------
// WebView-based upload (uses fetch with credentials: "include" for HttpOnly cookies)
// ---------------------------------------------------------------------------
async function uploadViaWebView(filePath: string, fileName: string): Promise<string | null> {
  const { readFile } = await import("@tauri-apps/plugin-fs");
  const bytes = await readFile(filePath);
  const blob = new Blob([bytes], { type: "application/octet-stream" });
  const file = new File([blob], fileName);
  const formData = new FormData();
  formData.append("file", file);

  let resp = await fetch("/api/upload", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  // Auto-refresh on 401 and retry
  if (resp.status === 401) {
    const refreshResp = await fetch("/api/ss/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (refreshResp.ok) {
      const retryForm = new FormData();
      retryForm.append("file", file);
      resp = await fetch("/api/upload", {
        method: "POST",
        credentials: "include",
        body: retryForm,
      });
    }
  }

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
    throw new Error(errorData.detail || `Upload failed (${resp.status})`);
  }

  // Capture job ID for status tracking
  const data = await resp.json().catch(() => null);
  return data?.jobId || null;
}

async function pollJobUntilDone(jobId: string): Promise<{ status: string; error?: string }> {
  const maxWait = 300000; // 5 minutes
  const interval = 3000;  // 3 seconds
  const start = Date.now();

  while (Date.now() - start < maxWait) {
    try {
      const resp = await fetch(`/api/jobs/${jobId}`, { credentials: "include" });
      if (!resp.ok) break;
      const data = await resp.json();
      if (data.status === "complete") return { status: "complete" };
      if (data.status === "failed") return { status: "failed", error: data.error };
    } catch { /* network blip, keep polling */ }
    await new Promise((r) => setTimeout(r, interval));
  }
  return { status: "timeout", error: "Processing timed out" };
}

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------
const uploadQueue: Array<{ path: string; name: string }> = [];
const retryQueue: Array<RetryFile> = [];
let isProcessingQueue = false;
let isProcessingRetries = false;

async function processUploadQueue(
  get: () => DesktopState,
  set: (partial: Partial<DesktopState>) => void
) {
  if (isProcessingQueue) return;
  isProcessingQueue = true;

  while (uploadQueue.length > 0) {
    set({ queueCount: uploadQueue.length });
    const file = uploadQueue.shift()!;

    // Skip if already uploaded
    if (getUploadedFiles().has(file.name)) {
      set({ queueCount: uploadQueue.length });
      continue;
    }

    set({ statusMessage: `Uploading: ${file.name}`, uploadProgress: 0 });

    try {
      const jobId = await uploadViaWebView(file.path, file.name);

      // Poll for processing completion if we got a job ID
      if (jobId) {
        set({ statusMessage: `Processing: ${file.name}`, uploadProgress: 50 });
        const result = await pollJobUntilDone(jobId);
        if (result.status === "failed") {
          void remoteLog("Processing failed", "error", { name: file.name, error: result.error });
          set({ lastError: `Processing failed: ${file.name} — ${result.error}` });
        }
      }

      markFileUploaded(file.name);
      void remoteLog("Upload complete", "info", { name: file.name });
      set({
        statusMessage: "Upload Success",
        uploadProgress: null,
        lastSuccess: new Date().toLocaleTimeString(),
        queueCount: uploadQueue.length
      });
    } catch (err) {
      void remoteLog("Upload failed, moving to retry queue", "warn", { name: file.name, error: String(err) });

      // Add to retry queue [OT-083]
      retryQueue.push({
        path: file.path,
        name: file.name,
        retries: 0,
        nextAttempt: Date.now() + 30000 // First retry in 30s
      });

      set({
        statusMessage: `Upload Failed: ${file.name}`,
        uploadProgress: null,
        lastError: `${new Date().toLocaleTimeString()}: ${String(err)}`,
        queueCount: uploadQueue.length,
        retryQueueCount: retryQueue.length
      });
    }

    await new Promise(r => setTimeout(r, 1000));
  }

  isProcessingQueue = false;
  set({ queueCount: 0 });

  // Start retry processor if not running
  void startRetryProcessor(get, set);

  // Reset status after queue drains
  setTimeout(() => {
    const current = get();
    if (current.uploadProgress === null && !isProcessingQueue) {
      set({ statusMessage: current.isWatching ? "Monitoring..." : "Agent Standby" });
    }
  }, 3000);
}

async function startRetryProcessor(
  _get: () => DesktopState,
  set: (partial: Partial<DesktopState>) => void
) {
  if (isProcessingRetries) return;
  isProcessingRetries = true;

  while (retryQueue.length > 0) {
    const now = Date.now();
    const readyIdx = retryQueue.findIndex(f => f.nextAttempt <= now);

    if (readyIdx === -1) {
      // Nothing ready, wait a bit
      set({ retryQueueCount: retryQueue.length });
      await new Promise(r => setTimeout(r, 10000));
      continue;
    }

    const file = retryQueue.splice(readyIdx, 1)[0];
    set({ retryQueueCount: retryQueue.length });

    // Skip if already uploaded
    if (getUploadedFiles().has(file.name)) continue;

    set({ statusMessage: `Retrying: ${file.name}`, uploadProgress: 0 });

    try {
      const jobId = await uploadViaWebView(file.path, file.name);

      if (jobId) {
        set({ statusMessage: `Processing: ${file.name}`, uploadProgress: 50 });
        await pollJobUntilDone(jobId);
      }

      markFileUploaded(file.name);
      void remoteLog("Retry upload successful", "info", { name: file.name, attempt: file.retries + 1 });
      set({
        statusMessage: "Upload Success",
        uploadProgress: null,
        lastSuccess: new Date().toLocaleTimeString()
      });
    } catch (err) {
      file.retries += 1;
      if (file.retries >= 5) {
        void remoteLog("Retry limit reached, discarding file", "error", { name: file.name });
        set({ lastError: `Retry limit reached for ${file.name}` });
      } else {
        // Exponential backoff: 30s, 2m, 8m, 30m...
        const backoff = Math.pow(4, file.retries) * 30000;
        file.nextAttempt = Date.now() + backoff;
        retryQueue.push(file);
        void remoteLog("Retry failed, backing off", "warn", { name: file.name, nextAttempt: new Date(file.nextAttempt).toLocaleTimeString() });
      }

      set({
        uploadProgress: null,
        lastError: `Retry failed for ${file.name}: ${String(err)}`,
        retryQueueCount: retryQueue.length
      });
    }

    await new Promise(r => setTimeout(r, 2000));
  }

  isProcessingRetries = false;
}

function enqueueUpload(
  file: { path: string; name: string },
  get: () => DesktopState,
  set: (partial: Partial<DesktopState>) => void
) {
  // Skip duplicates already in queue
  if (uploadQueue.some(f => f.name === file.name)) return;
  if (getUploadedFiles().has(file.name)) return;

  uploadQueue.push(file);
  set({ queueCount: uploadQueue.length });
  void processUploadQueue(get, set);
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface DesktopState {
  isTauri: boolean;
  isWatching: boolean;
  telemetryPath: string;
  statusMessage: string;
  uploadProgress: number | null;
  telemetryEnabled: boolean | null;
  lastSuccess: string | null;
  lastError: string | null;
  queueCount: number;
  retryQueueCount: number;

  setWatching: (watching: boolean) => void;
  setTelemetryPath: (path: string) => void;
  setStatusMessage: (msg: string) => void;
  setUploadProgress: (progress: number | null) => void;
  initialize: () => Promise<void>;
  startWatching: () => Promise<void>;
  stopWatching: () => Promise<void>;
  scanAndUpload: () => Promise<void>;
  checkTelemetry: () => Promise<void>;
  enableTelemetry: () => Promise<void>;
  pruneOldFiles: (maxAgeDays: number) => Promise<void>;
}

const isTauriEnvironment = (): boolean => {
  if (typeof window === "undefined") return false;

  // Check persistence first
  if (localStorage.getItem("osc_is_tauri") === "true") return true;

  const detected = (
    "__TAURI_INTERNALS__" in window ||
    "__TAURI__" in window ||
    "__TAURI_DETECTED__" in window ||
    (window as any).isTauri === true ||
    (typeof (window as any).__TAURI_POST_MESSAGE__ === "function") ||
    new URLSearchParams(window.location.search).has("desktop") ||
    navigator.userAgent.includes("OSC-Desktop-Agent")
  );

  if (detected) {
    localStorage.setItem("osc_is_tauri", "true");
    void remoteLog("Desktop mode first-time detection", "info", {
      url: window.location.href
    });
  }
  return detected;
};

export const useDesktopStore = create<DesktopState>((set, get) => ({
  isTauri: isTauriRuntime() || isTauriEnvironment(),
  isWatching: false,
  telemetryPath: "",
  statusMessage: "Agent Standby",
  uploadProgress: null,
  telemetryEnabled: null,
  lastSuccess: null,
  lastError: null,
  queueCount: 0,
  retryQueueCount: 0,

  setWatching: (watching) => set({ isWatching: watching }),
  setTelemetryPath: async (path) => {
    set({ telemetryPath: path });
    localStorage.setItem("osc_telemetry_path", path);

    // [OT-075/100] Auto-prune (Opt-in)
    const { useAuthStore } = await import("./authStore");
    const userPrefs = useAuthStore.getState().user?.preferences;
    if (userPrefs?.autoDeleteIbt) {
      void get().pruneOldFiles(userPrefs.retentionMonths * 30);
    }

    // Restart watcher if path changes
    if (get().isWatching) {
      get().startWatching();
    }
  },
  setStatusMessage: (msg) => set({ statusMessage: msg }),
  setUploadProgress: (progress) => set({ uploadProgress: progress }),

  initialize: async () => {
    // Re-check detection in case it was late or persisted since store creation
    const tauriDetected = isTauriRuntime() || isTauriEnvironment();
    if (tauriDetected && !get().isTauri) {
      set({ isTauri: true });
    }

    if (!tauriDetected) return;
    if (isInitialized) return;
    isInitialized = true;

    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const { listen } = await import("@tauri-apps/api/event");

      // 1. Load saved path or get default from Rust
      const savedPath = localStorage.getItem("osc_telemetry_path");
      if (savedPath) {
        set({ telemetryPath: savedPath });
      } else {
        const defaultPath: string | null = await invoke("get_telemetry_dir");
        if (defaultPath) {
          set({ telemetryPath: defaultPath });
          localStorage.setItem("osc_telemetry_path", defaultPath);
        }
      }

      // 2. Setup listener for new files from Rust watcher
      removeFileListener?.();
      removeFileListener = await listen<FileCreatedPayload>("ibt-created", (event) => {
        void remoteLog("New .ibt detected by watcher", "info", { path: event.payload.path });

        if (!get().isWatching) return;

        enqueueUpload(
          { path: event.payload.path, name: event.payload.name },
          get,
          set
        );
      });

      // 3. Start watcher + catch-up scan if path exists
      if (get().telemetryPath) {
        await get().startWatching();
        // Catch-up scan: upload any .ibt files missed before watcher started
        void get().scanAndUpload();
      }

      // 4. Check if iRacing telemetry is actually enabled in app.ini
      void get().checkTelemetry();

      // 5. [OT-075/100] Auto-prune (Opt-in)
      const { useAuthStore } = await import("./authStore");
      const userPrefs = useAuthStore.getState().user?.preferences;
      if (userPrefs?.autoDeleteIbt && get().telemetryPath) {
        void get().pruneOldFiles(userPrefs.retentionMonths * 30);
      }

      // 6. Listen for upload progress from Rust [OT-008]
      await listen<{ name: string; progress: number }>("upload-progress", (event) => {
        const { name, progress } = event.payload;
        const current = get();
        if (current.statusMessage.includes(name)) {
          set({ uploadProgress: progress });
        }
      });
    } catch (err) {
      isInitialized = false;
      console.error("Failed to initialize desktop store", err);
    }
  },

  checkTelemetry: async () => {
    if (!get().isTauri) return;
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const isEnabled: boolean = await invoke("check_iracing_telemetry");
      set({ telemetryEnabled: isEnabled });
      
      if (!isEnabled) {
        void remoteLog("iRacing telemetry is disabled in app.ini, attempting auto-fix", "warn");
        try {
          await get().enableTelemetry();
          set({ telemetryEnabled: true });
          void remoteLog("iRacing telemetry auto-fixed successfully", "info");
        } catch (err) {
          void remoteLog("iRacing telemetry auto-fix failed", "error", { error: String(err) });
        }
      }
    } catch (err) {
      console.error("Failed to check iRacing telemetry", err);
    }
  },

  enableTelemetry: async () => {
    if (!get().isTauri) return;
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("enable_iracing_telemetry");
      set({ telemetryEnabled: true });
      void remoteLog("iRacing telemetry enabled via app.ini patch", "info");
    } catch (err) {
      void remoteLog("Failed to enable iRacing telemetry", "error", { error: String(err) });
      throw err;
    }
  },

  pruneOldFiles: async (maxAgeDays: number) => {
    const { isTauri, telemetryPath } = get();
    if (!isTauri || !telemetryPath) return;
    
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const count: number = await invoke("prune_old_telemetry", { 
        path: telemetryPath, 
        maxAgeDays
      });
      if (count > 0) {
        void remoteLog(`Auto-cleanup: deleted ${count} old .ibt files`, "info", { maxAgeDays });
      }
    } catch (err) {
      console.error("Prune failed", err);
    }
  },

  startWatching: async () => {
    const { isTauri, telemetryPath } = get();
    if (!isTauri || !telemetryPath) return;

    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("start_watcher", { path: telemetryPath });
      void remoteLog("Watcher started", "info", { path: telemetryPath });
      set({ isWatching: true, statusMessage: "Monitoring..." });
    } catch (err) {
      void remoteLog("Failed to start watcher", "error", { path: telemetryPath, error: String(err) });
      set({ isWatching: false, statusMessage: "Watcher Error" });
    }
  },

  stopWatching: async () => {
    if (!get().isTauri) return;
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("stop_watcher");
      void remoteLog("Watcher stopped", "info");
      set({ isWatching: false, statusMessage: "Agent Standby" });
    } catch (err) {
      void remoteLog("Failed to stop watcher", "error", { error: String(err) });
    }
  },

  // Scan telemetry directory and upload any .ibt files not yet uploaded
  scanAndUpload: async () => {
    const { telemetryPath } = get();
    if (!telemetryPath) return;

    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const files: IbtFileInfo[] = await invoke("list_ibt_files", { path: telemetryPath });

      const uploaded = getUploadedFiles();
      // Only catch-up files modified in the last 48 hours (avoid mass-uploading old history)
      const cutoff = Math.floor(Date.now() / 1000) - 48 * 3600;
      const missed = files.filter(f => !uploaded.has(f.name) && f.size > 0 && f.modified_epoch > cutoff);

      if (missed.length > 0) {
        void remoteLog("Catch-up scan found missed files", "info", {
          count: missed.length,
          files: missed.map(f => f.name)
        });

        for (const file of missed) {
          enqueueUpload({ path: file.path, name: file.name }, get, set);
        }
      } else {
        void remoteLog("Catch-up scan: all files already uploaded", "info", {
          totalFiles: files.length
        });
      }
    } catch (err) {
      void remoteLog("Catch-up scan failed", "error", { error: String(err) });
    }
  }
}));
